import { useState } from 'react'

import {
  Button,
  ErrorBanner,
  PRIORITY_LABELS,
  STATUS_LABELS,
  Select,
  TASK_PRIORITIES,
  TASK_STATUSES,
  pluralize,
  useBulkIssues,
  useRoster,
  type BulkSetInput,
  type Issue,
  type SprintView,
} from '@/features/issue'

/**
 * The bar that appears once rows are selected.
 *
 * Every action is one `POST /issues/bulk`, which the server applies inside a
 * single transaction — so a change set either lands on all of the selected
 * issues or on none of them, and a half-applied sprint move is not a state the
 * planner has to recover from.
 */
export function BulkActionBar({
  slug,
  issues,
  sprints,
  onDone,
  onClear,
}: {
  slug: string | null
  issues: Issue[]
  sprints: SprintView[]
  onDone: () => void
  onClear: () => void
}) {
  const bulk = useBulkIssues(slug)
  const { members } = useRoster(slug)
  const [pending, setPending] = useState<string | null>(null)

  if (issues.length === 0) return null

  const keys = issues.map((issue) => issue.issue_key ?? issue.id)

  const apply = (set: BulkSetInput, label: string) => {
    setPending(label)
    bulk.mutate(
      { issue_keys: keys, action: 'update', set },
      {
        onSuccess: () => {
          setPending(null)
          onDone()
        },
        onSettled: () => setPending(null),
      },
    )
  }

  const remove = () => {
    if (!window.confirm(`Delete ${pluralize(issues.length, 'issue')}? This cannot be undone.`)) return
    setPending('delete')
    bulk.mutate(
      { issue_keys: keys, action: 'delete' },
      {
        onSuccess: () => {
          setPending(null)
          onDone()
        },
        onSettled: () => setPending(null),
      },
    )
  }

  return (
    <div className="sticky bottom-0 z-20 mt-3 space-y-2">
      {bulk.error ? <ErrorBanner error={bulk.error} /> : null}

      <div className="flex flex-wrap items-center gap-2 rounded-card border border-ink-200 bg-white px-3 py-2 shadow-lg shadow-ink-950/5 dark:border-ink-700 dark:bg-ink-900">
        <span className="text-sm font-medium text-ink-800 dark:text-ink-100">
          {pluralize(issues.length, 'issue')} selected
        </span>

        <Select
          aria-label="Move to sprint"
          value=""
          disabled={Boolean(pending)}
          onChange={(event) => {
            const value = event.target.value
            if (!value) return
            apply({ sprint_id: value === 'backlog' ? null : value }, 'sprint')
          }}
          className="w-40 py-1 text-xs"
        >
          <option value="">Move to…</option>
          <option value="backlog">Backlog</option>
          {sprints.map((sprint) => (
            <option key={sprint.id} value={sprint.id}>
              {sprint.name}
            </option>
          ))}
        </Select>

        <Select
          aria-label="Set status"
          value=""
          disabled={Boolean(pending)}
          onChange={(event) => {
            const value = event.target.value
            if (value) apply({ status: value as BulkSetInput['status'] }, 'status')
          }}
          className="w-32 py-1 text-xs"
        >
          <option value="">Status…</option>
          {TASK_STATUSES.map((status) => (
            <option key={status} value={status}>
              {STATUS_LABELS[status]}
            </option>
          ))}
        </Select>

        <Select
          aria-label="Set priority"
          value=""
          disabled={Boolean(pending)}
          onChange={(event) => {
            const value = event.target.value
            if (value) apply({ priority: value as BulkSetInput['priority'] }, 'priority')
          }}
          className="w-32 py-1 text-xs"
        >
          <option value="">Priority…</option>
          {TASK_PRIORITIES.map((priority) => (
            <option key={priority} value={priority}>
              {PRIORITY_LABELS[priority]}
            </option>
          ))}
        </Select>

        <Select
          aria-label="Set assignee"
          value=""
          disabled={Boolean(pending)}
          onChange={(event) => {
            const value = event.target.value
            if (!value) return
            apply({ assignee_id: value === 'none' ? null : Number(value) }, 'assignee')
          }}
          className="w-40 py-1 text-xs"
        >
          <option value="">Assign to…</option>
          <option value="none">Unassigned</option>
          {members.map((member) => (
            <option key={member.user_id} value={String(member.user_id)}>
              {member.user?.name ?? `User ${member.user_id}`}
            </option>
          ))}
        </Select>

        <Button variant="danger" size="sm" onClick={remove} disabled={Boolean(pending)}>
          Delete
        </Button>

        <Button variant="ghost" size="sm" onClick={onClear} className="ml-auto">
          Clear selection
        </Button>
      </div>
    </div>
  )
}
